// src/encounter/growth-chart-print-dialog.tsx
// Host for the DS `GrowthChartPrintPreview`. Port of what Flutter's growth-chart print icon opens:
// `PopUpUtils.showSimpleDialog(... SizedBox(height: 80% of screen, width: 822,
// child: GrowthChartPrintPreviewWidget(...)))` — `growth_chart.dart:1319-1336`.
//
// Flutter prints by rasterising the widget to a PDF named
// `<FullNameNoSpaces>_GrowthChartPrintPreview` (`print_growth_chart_preview.dart:52-58`). On the web
// the equivalent is the browser's own print pipeline, so this calls `window.print()` and carries the
// `@media print` rule that makes the sheet the only thing on the page. The rule lives here rather
// than in a global stylesheet because it exists only while this dialog is mounted — a print
// triggered from anywhere else in the app must not be affected by it.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GrowthChartPrintPreview, type GrowthChartProps } from '@scripsteam/scrips-signal-ds';
import { usePractitionerPractices } from '@/scheduling/services/use-practitioner-practices';

/** Only what the sheet shows — the caller already has these on the page. */
export interface GrowthChartPrintDialogProps {
  open: boolean;
  onClose: () => void;
  patient: { fullName?: string | null; mrn?: string | null; genderName?: string | null; age?: string | null };
  chart: Omit<GrowthChartProps, 'onPrint' | 'className'>;
  /** doctorSetupId — the practice list is fetched per practitioner. */
  practitionerId?: string | null;
  /** Which of that practitioner's practices this encounter belongs to. */
  practiceId?: string | null;
}

const PRINT_ROOT = 'growth-chart-print-root';

export function GrowthChartPrintDialog({
  open,
  onClose,
  patient,
  chart,
  practitionerId,
  practiceId,
}: GrowthChartPrintDialogProps) {
  // Fetched HERE rather than on the encounter page: a `useQuery` on that page breaks all 13
  // encounter-shell-page suites at once ("No QueryClient set"), because they mock every service
  // hook and a newly-added real one becomes the only live query in the tree. This component mounts
  // only when the print dialog is open, so an encounter that never prints never makes the call.
  const { data: practices } = usePractitionerPractices(open ? practitionerId : undefined);
  const row = (practices ?? []).find((p) => p.practiceSetupId === practiceId);
  // `phone` is not on the ForPractitioner shape, so the sheet renders "-" for it — which is also
  // what Flutter renders when the backend gives null (`print_growth_chart_preview.dart:113`).
  // Noted on PROD-2044 rather than faked from another field.
  const practice = { name: row?.practiceName, address: row?.address };
  // Escape closes. Flutter's dialog is dismissible (`showSimpleDialog`, unlike the sign-note
  // confirmation which sets barrierDismissible: false) — nothing is written by printing.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        @media print {
          body > *:not(#${PRINT_ROOT}) { display: none !important; }
          #${PRINT_ROOT} {
            position: static !important;
            inset: auto !important;
            background: none !important;
            padding: 0 !important;
            display: block !important;
          }
          #${PRINT_ROOT} [data-print-sheet] {
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: 0 !important;
            width: auto !important;
          }
        }
      `}</style>
      <div
        id={PRINT_ROOT}
        role="dialog"
        aria-modal="true"
        aria-label="Growth chart print preview"
        // z-1000: no DS z-index scale to resolve to (a Tailwind integer z utility, not a literal).
        // The DS overlay token at the modal-scrim opacity — no raw colour literal.
        className="fixed inset-0 z-1000 flex items-center justify-center p-6 bg-surface-overlay/45"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          data-print-sheet
          // Flutter's dialog is 822px wide (w-205.5 on the 4px spacing step) and 80% of the viewport
          // tall (`:1322-1324`). No DS width token at this size; `80vh` has no named utility.
          className="w-205.5 max-w-full max-h-print-dialog overflow-y-auto bg-surface-card border border-border-default rounded-lg shadow-6 p-4"
        >
          <GrowthChartPrintPreview
            patient={patient}
            practice={practice}
            chart={chart}
            onClose={onClose}
            onPrint={() => window.print()}
          />
        </div>
      </div>
    </>,
    document.body,
  );
}
